import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { Check, ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils.ts"
import type { Identified } from "@/types/commonTypes.ts"

import { Button } from "./ui/button.tsx"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover.tsx"

export default function EntitySelector<T extends Identified>({
  value,
  setValue,
  displayProp,
  fetcher,
  queryKey,
}: {
  value: number
  setValue: (id: number) => void
  displayProp: keyof T
  fetcher: () => Promise<T[]>
  queryKey: string
}) {
  const [open, setOpen] = useState<boolean>(false)
  const { data } = useQuery({
    queryKey: [queryKey],
    queryFn: fetcher,
  })
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full flex justify-between">
          {(data?.find((e) => e.id == value)?.[displayProp] as string) || "Select an item"}
          <ChevronDown className="opacity-50 stroke-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] z-200">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No items found.</CommandEmpty>
            <CommandGroup>
              {data?.map((e) => (
                <CommandItem
                  value={e[displayProp] as string}
                  key={e.id}
                  onSelect={() => {
                    setOpen(false)
                    setValue(e.id)
                  }}
                >
                  {e[displayProp] as string}
                  <Check className={cn("ml-auto ", e.id == value ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
